import React, { useState, useRef, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ControlOutlined,
} from '@ant-design/icons';

import {
  ViewerContainer,
  ViewerLabel,
  MediaContainer,
  MediaWrapper,
  StyledVideo,
  StyledImg,
  SegmentationOverlay,
  SegmentationLabel,
  ToggleButtonWrapper,
  DownloadDicomButtonWrapper,
  ViewOnReviewerButtonWrapper,
  KeypointsLabel,
  FrameControlsBar,
  FrameControlButton,
  FrameCounter,
  ScrubBarTrack,
  PredictedViewBadge,
} from './styled';
import { toast } from 'react-toastify';
import { config } from 'services/config';
import KeypointOverlay from './KeypointOverlay';
import { useReextractFramesMutation } from 'services/dicomService';

export interface ViewerMedia {
  signed_url: string;
  extension: string;
  icid: string;
}

export interface Keypoint {
  id: number;
  icid: string;
  x: number;
  y: number;
  keypoint_class_id: string;
  keypoint_collection_id: string;
  frame: { uuid: string; signed_url: string };
  keypoint_class?: { value: string; uuid?: string; keypoint_pair_id?: string | null };
  keypoint_collection_icid?: string;
}

export interface ViewerProps {
  mediaData: ViewerMedia;
  dicomDownloadUrl?: string;
  segmentationData?: {
    signed_url?: string | null;
    icid: string;
    paths?: string[];
    frameUrl?: string;
  } | null;
  onClearSegmentation?: () => void;
  dicom_Id?: string;
  keypoints?: Keypoint[];
  onClearKeypoints?: () => void;
  /** Total frames for this DICOM (for scrub bar and counter). For video, used as hint; actual range from duration. */
  numberOfFrames?: number;
  /** Predicted view name to show in corner */
  predictedView?: string | null;
  /** When true, show predicted view badge, use a taller media area, and hide segmentation/keypoint toggles and download controls. */
  enhanced?: boolean;
  onDownloadFullStudy?: () => void;
  isDownloadingFullStudy?: boolean;
  /** Called when the media fails to load, so the page can try its next source. */
  onMediaError?: () => void;
  /** Download through the API's signed attachment links instead of dicomDownloadUrl / fetch. */
  onDownloadFile?: (kind: 'dicom' | 'video' | 'image') => Promise<void> | void;
}

const extensionToType: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  mp4: 'video/mp4',
  mov: 'video/mp4',
  avi: 'video/x-msvideo',
  dcm: 'application/dicom',
};

/** Remembered per browser; storage can be unavailable (private mode), so failures fall back to off. */
const CONTROLS_KEY = 'atlas.viewer.nativeControls';
const readControlsPref = () => {
  try {
    return window.localStorage.getItem(CONTROLS_KEY) === '1';
  } catch {
    return false;
  }
};
const writeControlsPref = (on: boolean) => {
  try {
    window.localStorage.setItem(CONTROLS_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
};

const FRAME_WHEEL_STEP = 0.5; // seconds for video seek per wheel tick

const Viewer: React.FC<ViewerProps> = ({
  mediaData,
  dicomDownloadUrl,
  segmentationData,
  onClearSegmentation,
  dicom_Id,
  keypoints = [],
  onClearKeypoints,
  numberOfFrames = 1,
  predictedView,
  enhanced = false,
  onDownloadFullStudy,
  isDownloadingFullStudy,
  onMediaError,
  onDownloadFile,
}) => {
  const mediaKey = mediaData.signed_url;
  // Keyed to the URL that finished loading rather than a boolean reset in an effect: a cached
  // image can fire `load` before a reset effect runs, which left the spinner up for good.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const mediaLoaded = loadedKey === mediaKey;
  const markLoaded = () => setLoadedKey(mediaKey);
  const [bufferedPct, setBufferedPct] = useState<number | null>(null);
  useEffect(() => {
    setBufferedPct(null);
  }, [mediaKey]);
  const [showSegmentation, setShowSegmentation] = useState(true);
  const mediaWrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [videoState, setVideoState] = useState({
    duration: 0,
    currentTime: 0,
    isPlaying: false,
  });
  const [currentFrameIndex, setCurrentFrameIndex] = useState(1); // 1-based for display (image or single frame)
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadDicom = async () => {
    if (onDownloadFile) {
      await onDownloadFile('dicom');
      return;
    }
    if (!dicomDownloadUrl) {
      toast.error('No DICOM download URL available', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    const link = document.createElement('a');
    link.href = dicomDownloadUrl;
    link.download = `dicom-${dicom_Id || 'file'}.dcm`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('DICOM download started', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleDownloadMp4 = async () => {
    if (onDownloadFile) {
      setIsDownloading(true);
      try {
        await onDownloadFile(isVideoMedia ? 'video' : 'image');
      } finally {
        if (isMounted.current) setIsDownloading(false);
      }
      return;
    }
    if (!mediaData.signed_url) {
      toast.error('No MP4 URL available', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsDownloading(true);
    let infoToastId: string | number | null = null;
    try {
      infoToastId = toast.info('Preparing MP4 for download...', {
        position: 'top-right',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      const response = await fetch(mediaData.signed_url);
      if (!response.ok) throw new Error('Fetch failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `video-${dicom_Id || mediaData.icid || 'file'}.mp4`;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);

      toast.success('MP4 download started', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (err) {
      console.error('Download failed:', err);

      toast.error('Failed to download MP4 directly. The browser may block this because of CORS.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      if (infoToastId) toast.dismiss(infoToastId);
      if (isMounted.current) {
        setIsDownloading(false);
      }
    }
  };

  const [reextractFrames, { isLoading: reextracting }] = useReextractFramesMutation();

  const handleReextractFrames = async () => {
    if (!dicom_Id) {
      toast.error('No DICOM identifier available for re-extraction');
      return;
    }
    try {
      const result = await reextractFrames(dicom_Id).unwrap();
      toast.success(`Re-extraction started — uploaded ${result?.count || 0} frames`);
    } catch (err: unknown) {
      const e = err as any;
      const msg =
        (e?.data && (e.data.detail || e.data.message)) || e?.error || e?.message || String(e);
      toast.error(`Re-extraction failed: ${msg}`);
    }
  };

  const handleViewOnReviewer = () => {
    if (!segmentationData) return;

    const token = localStorage.getItem('access_token'); // Adjust this key if needed
    if (!token) {
      toast.error('No authentication token found', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    const url = `${config.REVIEWER_URL}/segmentation-labels?segmentationICID=${encodeURIComponent(
      segmentationData.icid
    )}`;
    window.open(url, '_blank');
  };

  if (!mediaData.signed_url) return null;
  const mediaType = extensionToType[mediaData.extension.toLowerCase()] || '';
  const isVideoMedia = mediaType.startsWith('video/');
  const [nativeControls, setNativeControls] = useState<boolean>(readControlsPref);
  const toggleNativeControls = () =>
    setNativeControls(on => {
      writeControlsPref(!on);
      return !on;
    });
  const totalFrames = isVideoMedia
    ? Math.max(1, numberOfFrames > 0 ? numberOfFrames : Math.round((videoState.duration || 0) * 30))
    : Math.max(1, numberOfFrames);
  const duration = videoState.duration;
  const currentTime = videoState.currentTime;
  const displayFrameIndex = isVideoMedia
    ? Math.min(
        totalFrames,
        Math.max(1, duration > 0 ? Math.round((currentTime / duration) * totalFrames) || 1 : 1)
      )
    : currentFrameIndex;
  const scrubValue =
    totalFrames <= 1
      ? 1
      : isVideoMedia && duration > 0
        ? Math.max(0, Math.min(1, currentTime / duration))
        : (displayFrameIndex - 1) / (totalFrames - 1);

  useEffect(() => {
    const wrapper = mediaWrapperRef.current;
    if (!isVideoMedia || !wrapper) return;
    const onWheel = (e: WheelEvent) => {
      const v = videoRef.current;
      if (!v || !isFinite(v.duration) || v.duration <= 0) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? FRAME_WHEEL_STEP : -FRAME_WHEEL_STEP;
      const next = Math.max(0, Math.min(v.duration, v.currentTime + delta));
      v.currentTime = next;
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', onWheel);
  }, [isVideoMedia, mediaKey]);

  useEffect(() => {
    if (!isVideoMedia || !videoRef.current) return;
    const v = videoRef.current;
    const onDurationChange = () => setVideoState(prev => ({ ...prev, duration: v.duration }));
    const onPlay = () => setVideoState(prev => ({ ...prev, isPlaying: true }));
    const onPause = () => setVideoState(prev => ({ ...prev, isPlaying: false }));
    const onProgress = () => {
      if (!v.duration || !isFinite(v.duration) || !v.buffered.length) return;
      setBufferedPct(
        Math.min(100, Math.round((100 * v.buffered.end(v.buffered.length - 1)) / v.duration))
      );
    };
    v.addEventListener('progress', onProgress);
    v.addEventListener('durationchange', onDurationChange);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    if (v.duration) setVideoState(prev => ({ ...prev, duration: v.duration }));
    return () => {
      v.removeEventListener('durationchange', onDurationChange);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('progress', onProgress);
    };
  }, [isVideoMedia, mediaKey]);

  useEffect(() => {
    if (!isVideoMedia || !videoRef.current) return;
    let rafId = 0;
    const tick = () => {
      const v = videoRef.current;
      if (!v) return;
      const t = v.currentTime;
      setVideoState(prev => (prev.currentTime === t ? prev : { ...prev, currentTime: t }));
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isVideoMedia, mediaKey]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (videoState.isPlaying) videoRef.current.pause();
    else videoRef.current.play();
  };

  const stepFrames = (direction: 1 | -1) => {
    if (isVideoMedia && videoRef.current && videoState.duration > 0) {
      const step = videoState.duration / Math.max(totalFrames, 1);
      const next = Math.max(
        0,
        Math.min(videoState.duration, videoState.currentTime + direction * step)
      );
      videoRef.current.currentTime = next;
    } else if (!isVideoMedia && totalFrames > 1) {
      setCurrentFrameIndex(prev => Math.max(1, Math.min(totalFrames, prev + direction)));
    }
  };

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const p = Number(e.target.value);
    if (isVideoMedia && videoRef.current && videoState.duration > 0) {
      videoRef.current.currentTime = p * videoState.duration;
    } else {
      const frame = Math.round(p * (totalFrames - 1)) + 1;
      setCurrentFrameIndex(Math.max(1, Math.min(totalFrames, frame)));
    }
  };

  const goToStart = () => {
    if (isVideoMedia && videoRef.current) videoRef.current.currentTime = 0;
    else setCurrentFrameIndex(1);
  };

  const goToEnd = () => {
    if (isVideoMedia && videoRef.current && videoState.duration > 0)
      videoRef.current.currentTime = videoState.duration;
    else setCurrentFrameIndex(totalFrames);
  };

  return (
    <ViewerContainer $enhanced={enhanced}>
      <ViewerLabel>
        {enhanced
          ? `DICOM ${dicom_Id}`
          : segmentationData
            ? `Segment – ${segmentationData.icid}`
            : keypoints && keypoints.length > 0
              ? `${keypoints[0]?.keypoint_collection_icid || ''}`
              : `DICOM ${dicom_Id}`}

        {!enhanced && segmentationData && (
          <Tooltip title="Clear segmentation">
            <Button
              shape="circle"
              size="small"
              type="text"
              style={{ color: 'var(--ink-2)' }}
              icon={<CloseCircleOutlined style={{ fontSize: 16 }} />}
              onClick={onClearSegmentation}
            />
          </Tooltip>
        )}
        {!enhanced && keypoints && keypoints.length > 0 && (
          <Tooltip title="Clear keypoints">
            <Button
              shape="circle"
              size="small"
              type="text"
              style={{ color: 'var(--ink-2)' }}
              icon={<CloseCircleOutlined style={{ fontSize: 16 }} />}
              onClick={onClearKeypoints}
            />
          </Tooltip>
        )}
      </ViewerLabel>

      {enhanced && predictedView && (
        <PredictedViewBadge title={predictedView}>{predictedView}</PredictedViewBadge>
      )}

      <MediaContainer>
        <MediaWrapper
          className="ax-media-wrap"
          style={enhanced ? { height: '65vh', maxHeight: 700 } : undefined}
        >
          {mediaType.startsWith('video/') ? (
            <StyledVideo
              ref={videoRef}
              key={mediaKey}
              onLoadedData={markLoaded}
              onError={() => onMediaError?.()}
              autoPlay
              loop
              muted
              playsInline
              controls={nativeControls}
            >
              <source
                src={mediaData.signed_url}
                type={mediaType}
                onError={() => onMediaError?.()}
              />
              Your browser does not support video playback.
            </StyledVideo>
          ) : (
            <StyledImg
              key={mediaKey}
              src={mediaData.signed_url}
              alt="Medical media"
              referrerPolicy="no-referrer"
              onLoad={markLoaded}
              onError={() => onMediaError?.()}
            />
          )}

          {!mediaLoaded && (
            <div className="ax-media-loading" role="status" aria-live="polite">
              <span className="ax-spinner" aria-hidden />
              <span>
                {isVideoMedia ? 'Loading clip' : 'Loading image'}
                {bufferedPct != null ? ` · ${bufferedPct}%` : '…'}
              </span>
            </div>
          )}

          {/* Keypoint overlay */}
          {keypoints && keypoints.length > 0 && (
            <KeypointOverlay key={mediaData.icid} keypoints={keypoints} />
          )}

          {/* Keypoint icid label at bottom left, if no segmentation overlay */}
          {keypoints && keypoints.length > 0 && !segmentationData && (
            <KeypointsLabel>
              <span style={{ marginLeft: 16 }}>{keypoints[0]?.keypoint_collection_icid || ''}</span>
            </KeypointsLabel>
          )}

          {segmentationData && showSegmentation && (
            <>
              {/* If paths are provided, render them as an SVG overlay positioned over the image */}
              {segmentationData.paths && segmentationData.paths.length ? (
                <div
                  ref={mediaWrapperRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                  }}
                >
                  {/* Load the image to compute scaling/offset for object-fit: contain */}
                  <SegmentationPathsRenderer
                    frameUrl={segmentationData.frameUrl ?? mediaData.signed_url}
                    paths={segmentationData.paths}
                  />
                  <SegmentationLabel>{segmentationData.icid}</SegmentationLabel>
                  <ViewOnReviewerButtonWrapper>
                    <Button size="small" onClick={handleViewOnReviewer}>
                      View on reviewer
                    </Button>
                  </ViewOnReviewerButtonWrapper>
                </div>
              ) : (
                <>
                  {segmentationData.signed_url ? (
                    <SegmentationOverlay
                      src={segmentationData.signed_url}
                      alt="Segmentation overlay"
                    />
                  ) : null}
                  <SegmentationLabel>{segmentationData.icid}</SegmentationLabel>
                  <ViewOnReviewerButtonWrapper>
                    <Button size="small" onClick={handleViewOnReviewer}>
                      View on reviewer
                    </Button>
                  </ViewOnReviewerButtonWrapper>
                </>
              )}
            </>
          )}

          {segmentationData && (
            <ToggleButtonWrapper>
              <Tooltip title={showSegmentation ? 'Hide segmentation' : 'Show segmentation'}>
                <Button
                  shape="circle"
                  size="small"
                  icon={
                    showSegmentation ? (
                      <EyeOutlined style={{ fontSize: 14 }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ fontSize: 14 }} />
                    )
                  }
                  onClick={e => {
                    e.stopPropagation();
                    setShowSegmentation(!showSegmentation);
                  }}
                />
              </Tooltip>
            </ToggleButtonWrapper>
          )}
        </MediaWrapper>
      </MediaContainer>

      <FrameControlsBar>
        <FrameControlButton
          type="button"
          onClick={goToStart}
          disabled={totalFrames <= 1}
          title="Go to start"
          aria-label="Go to start"
        >
          ‹‹
        </FrameControlButton>
        <FrameControlButton
          type="button"
          onClick={() => stepFrames(-1)}
          disabled={totalFrames <= 1}
          title="Previous frame"
          aria-label="Previous frame"
        >
          ‹
        </FrameControlButton>
        <FrameControlButton
          type="button"
          onClick={handlePlayPause}
          disabled={!isVideoMedia}
          title={videoState.isPlaying ? 'Pause' : 'Play'}
          aria-label={videoState.isPlaying ? 'Pause' : 'Play'}
        >
          {videoState.isPlaying ? '❚❚' : '▶'}
        </FrameControlButton>
        <FrameControlButton
          type="button"
          onClick={() => stepFrames(1)}
          disabled={totalFrames <= 1}
          title="Next frame"
          aria-label="Next frame"
        >
          ›
        </FrameControlButton>
        <FrameControlButton
          type="button"
          onClick={goToEnd}
          disabled={totalFrames <= 1}
          title="Go to end"
          aria-label="Go to end"
        >
          ››
        </FrameControlButton>
        <ScrubBarTrack
          min={0}
          max={1}
          step="any"
          value={scrubValue}
          onChange={handleScrub}
          disabled={totalFrames <= 1}
          aria-label="Frame scrubber"
        />
        <FrameCounter>
          {displayFrameIndex} / {totalFrames}
        </FrameCounter>
        {isVideoMedia && (
          <FrameControlButton
            type="button"
            onClick={toggleNativeControls}
            aria-pressed={nativeControls}
            title={
              nativeControls
                ? 'Hide video controls'
                : 'Show video controls (speed, fullscreen, picture-in-picture)'
            }
            aria-label={nativeControls ? 'Hide video controls' : 'Show video controls'}
            style={
              nativeControls ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined
            }
          >
            <ControlOutlined />
          </FrameControlButton>
        )}
      </FrameControlsBar>

      <DownloadDicomButtonWrapper>
        {onDownloadFullStudy && (
          <Tooltip title="Download ALL DICOMs for this study">
            <Button
              icon={<DownloadOutlined />}
              onClick={onDownloadFullStudy}
              loading={isDownloadingFullStudy}
              style={{ marginRight: 8 }}
            >
              Download Full Study
            </Button>
          </Tooltip>
        )}
        {isVideoMedia && (
          <Tooltip title={mediaData.signed_url ? 'Download MP4 Video' : 'No MP4 available'}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadMp4}
              loading={isDownloading}
              disabled={!mediaData.signed_url || isDownloading}
              style={{ marginRight: 8 }}
            >
              Download MP4
            </Button>
          </Tooltip>
        )}
        <Tooltip
          title={
            dicomDownloadUrl || onDownloadFile
              ? 'Download Original DICOM File'
              : 'No DICOM file available'
          }
        >
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadDicom}
            disabled={!dicomDownloadUrl && !onDownloadFile}
          >
            Download DICOM File
          </Button>
        </Tooltip>
        <Tooltip title="Reextract frames from original DICOM">
          <Button
            style={{ marginLeft: 8 }}
            onClick={handleReextractFrames}
            loading={reextracting}
            disabled={!dicomDownloadUrl && !dicom_Id}
          >
            Reextract Frames
          </Button>
        </Tooltip>
      </DownloadDicomButtonWrapper>
    </ViewerContainer>
  );
};

export default Viewer;

// Helper component to render SVG paths over the displayed frame image.
const SegmentationPathsRenderer: React.FC<{
  frameUrl: string;
  paths?: string | string[];
}> = ({ frameUrl, paths }) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [imgDims, setImgDims] = useState({
    width: 1,
    height: 1,
    left: 0,
    top: 0,
    imgW: 1,
    imgH: 1,
    loaded: false,
  });

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const img = new window.Image();
    img.onload = function () {
      const containerW = el.offsetWidth;
      const containerH = el.offsetHeight;
      const imgW = img.naturalWidth;
      const imgH = img.naturalHeight;
      const scale = Math.min(containerW / imgW, containerH / imgH);
      const displayW = imgW * scale;
      const displayH = imgH * scale;
      const left = (containerW - displayW) / 2;
      const top = (containerH - displayH) / 2;
      setImgDims({ width: displayW, height: displayH, left, top, imgW, imgH, loaded: true });
    };
    img.src = frameUrl;

    const ro = new ResizeObserver(() => {
      if (img.complete) {
        const containerW = wrapperRef.current?.offsetWidth || 1;
        const containerH = wrapperRef.current?.offsetHeight || 1;
        const imgW = img.naturalWidth || 1;
        const imgH = img.naturalHeight || 1;
        const scale = Math.min(containerW / imgW, containerH / imgH);
        const displayW = imgW * scale;
        const displayH = imgH * scale;
        const left = (containerW - displayW) / 2;
        const top = (containerH - displayH) / 2;
        setImgDims({ width: displayW, height: displayH, left, top, imgW, imgH, loaded: true });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameUrl]);

  if (!imgDims.loaded) {
    return <div ref={wrapperRef} style={{ width: '100%', height: '100%' }} />;
  }

  const normalizePaths = (input?: string | string[]): string[] => {
    if (!input) return [];
    let arr: string[] = [];
    if (Array.isArray(input)) {
      arr = input.map(String);
    } else {
      const s = input.trim();
      if (s.startsWith('[')) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) arr = parsed.map(String);
          else arr = [String(parsed)];
        } catch {
          arr = [s];
        }
      } else {
        arr = [s];
      }
    }

    return arr.map(p => {
      let out = p.trim();
      out = out.replace(/([A-Za-z])\s*,\s*/g, '$1 ');
      out = out.replace(/,/g, ' ');
      out = out.replace(/\s+/g, ' ').trim();
      return out;
    });
  };

  const pathArray = normalizePaths(paths);

  return (
    <div
      ref={wrapperRef}
      style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: 'absolute',
          left: imgDims.left,
          top: imgDims.top,
          width: imgDims.width,
          height: imgDims.height,
          pointerEvents: 'none',
          zIndex: 1,
        }}
        viewBox={`0 0 ${imgDims.imgW} ${imgDims.imgH}`}
        preserveAspectRatio="xMinYMin meet"
      >
        {pathArray.map((p, i) => {
          const color = 'rgba(146, 22, 32, 0.95)';
          return <path key={i} d={p} fill={color} fillOpacity={0.45} stroke="none" />;
        })}
      </svg>
    </div>
  );
};
